import React from 'react';
import { Transition } from '@headlessui/react';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ExclamationTriangleIcon, 
  InformationCircleIcon, 
  XMarkIcon 
} from '@heroicons/react/24/outline';
import { useNotification } from '../../contexts/NotificationContext';

const iconMap = {
  success: CheckCircleIcon,
  error: XCircleIcon,
  warning: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

// Modern subtle color scheme
const bgColorMap = {
  success: 'bg-white/90 dark:bg-slate-800/90',
  error: 'bg-white/90 dark:bg-slate-800/90',
  warning: 'bg-white/90 dark:bg-slate-800/90',
  info: 'bg-white/90 dark:bg-slate-800/90',
};

// Accent colors for icons and indicators
const accentColorMap = {
  success: 'bg-emerald-500/10 text-emerald-500 dark:bg-emerald-500/20 dark:text-emerald-400',
  error: 'bg-rose-500/10 text-rose-500 dark:bg-rose-500/20 dark:text-rose-400',
  warning: 'bg-amber-500/10 text-amber-500 dark:bg-amber-500/20 dark:text-amber-400',
  info: 'bg-blue-500/10 text-blue-500 dark:bg-blue-500/20 dark:text-blue-400',
};

// Progress bar colors
const progressColorMap = {
  success: 'bg-emerald-500/80 dark:bg-emerald-400/80',
  error: 'bg-rose-500/80 dark:bg-rose-400/80',
  warning: 'bg-amber-500/80 dark:bg-amber-400/80',
  info: 'bg-blue-500/80 dark:bg-blue-400/80',
};

// Text colors
const textColorMap = {
  success: 'text-slate-700 dark:text-slate-200',
  error: 'text-slate-700 dark:text-slate-200',
  warning: 'text-slate-700 dark:text-slate-200',
  info: 'text-slate-700 dark:text-slate-200',
};

function NotificationContainer() {
  const { notifications, removeNotification } = useNotification();

  return (
    <div className="fixed inset-0 flex z-50 px-4 py-6 pointer-events-none sm:p-6">
      <div className="w-full flex flex-col items-center space-y-2.5 sm:items-end">
        {notifications.map((notification) => {
          const Icon = iconMap[notification.type] || iconMap.info;
          const type = notification.type || 'info';
          
          return (
            <Transition
              key={notification.id}
              show={true}
              enter="transform ease-out duration-300 transition"
              enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-4"
              enterTo="translate-y-0 opacity-100 sm:translate-x-0"
              leave="transition ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div
                className={`max-w-sm w-full pointer-events-auto overflow-hidden 
                  ${bgColorMap[type]} rounded-lg shadow-elevated backdrop-blur-lg 
                  border border-slate-200/40 dark:border-slate-700/40`}
                style={{ 
                  boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.05), 0 1px 3px 0 rgba(0, 0, 0, 0.1)' 
                }}
              >
                <div className="relative py-3.5 px-4">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 rounded-full p-1.5 ${accentColorMap[type]}`}>
                      <Icon
                        className="h-4 w-4"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-3 flex-1">
                      <p className={`text-sm font-medium ${textColorMap[type]}`}>
                        {notification.message}
                      </p>
                    </div>
                    <div className="ml-2 flex-shrink-0">
                      <button
                        className="rounded-full p-1 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-600
                        hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-150"
                        onClick={() => removeNotification(notification.id)}
                      >
                        <span className="sr-only">Close</span>
                        <XMarkIcon className="h-4 w-4 text-slate-400 dark:text-slate-500" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Elegant progress bar */}
                  <div 
                    className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 rounded-full ${progressColorMap[type]}`} 
                    style={{
                      width: '94%',
                      opacity: 0.8,
                      animation: `shrinkFromCenter ${notification.duration}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`
                    }}
                  />
                </div>
              </div>
            </Transition>
          );
        })}
      </div>
    </div>
  );
}

export default NotificationContainer;